// src/services/menuApi.js  (REAL, via redir.php, tenant-aware)

const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";

// ---- helpers ----
function normalizeTenantServerUrl(tenantServerUrl) {
  if (!tenantServerUrl) {
    throw new Error("menuApi: tenantServerUrl is required");
  }
  return tenantServerUrl.replace(/\/$/, "");
}

function menuRoot(tenantServerUrl) {
  const base = normalizeTenantServerUrl(tenantServerUrl);
  return `${base}/tablemate/endpoints/menu`;
}

function buildUrl(upstreamUrl) {
  // upstreamUrl is the raw tenant endpoint, e.g. https://TENANT/tablemate/endpoints/menu/...
  return `${REDIR_BASE}${encodeURIComponent(upstreamUrl)}`;
}

async function parseMaybeJSON(resp) {
  const text = await resp.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function getJSON(upstreamUrl, qs = {}) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(qs)) {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  }

  const full =
    buildUrl(upstreamUrl) + (usp.toString() ? `&${usp.toString()}` : "");

  const resp = await fetch(full, { method: "GET" });
  const payload = await parseMaybeJSON(resp);
  if (!resp.ok) {
    const msg = payload?.message || `HTTP ${resp.status}`;
    const err = new Error(msg);
    err.httpStatus = resp.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

async function postForm(upstreamUrl, dataObj) {
  const full = buildUrl(upstreamUrl);
  const form = new FormData();
  Object.entries(dataObj || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, String(v));
  });

  const resp = await fetch(full, { method: "POST", body: form });
  const payload = await parseMaybeJSON(resp);
  console.log("RESPONSE:", JSON.stringify(payload));

  if (!resp.ok || payload?.ok === false) {
    const msg = payload?.message || `HTTP ${resp.status}`;
    const err = new Error(msg);
    err.httpStatus = resp.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

// ---- field adapters (tolerant of your schema names) ----
function cryptoRandom() {
  return Math.random().toString(36).slice(2, 10);
}

function adaptCategory(row = {}) {
  const id = String(row.id ?? row.code ?? row.ID ?? row.CODE ?? cryptoRandom());
  const name = row.main ?? row.title ?? row.category_name ?? `Category ${id}`;
  const code = row.code ?? row.CODE ?? row.id ?? String(id);
  const visibility = row.visibility;
  const description = row.description;
  return { id, name, code, visibility, description, subcategories: [] };
}

function adaptSubcategory(row = {}) {
  const subcode = row.subcode ?? row.SUBCODE ?? row.code ?? row.id ?? null;
  const id = String(row.id ?? subcode ?? cryptoRandom());
  const name =
    row.main ?? row.title ?? row.subcategory_name ?? `Subcategory ${id}`;
  const visibility = row.visibility;
  const description = row.description;
  return {
    id,
    name,
    subcode: String(subcode ?? id),
    description,
    visibility,
    items: [],
  };
}

function adaptItem(row = {}) {
  const id = String(row.id ?? row.item_id ?? cryptoRandom());
  const name = row.item ?? row.title ?? row.item_name ?? `Item ${id}`;
  const price = row.price ?? row.amount ?? row.cost ?? null;
  const image = row.image ?? row.photo ?? row.pic ?? null;

  const allergens =
    row.allergens_titles || row.allergens_descs || row.allergens_icons
      ? {
          titles: (row.allergens_titles || "")
            .split("--")
            .filter(Boolean),
          descs: (row.allergens_descs || "").split("--").filter(Boolean),
          icons: (row.allergens_icons || "").split("--").filter(Boolean),
        }
      : null;

  return {
    id,
    name,
    price,
    image,
    allergens,
    sides_resolved: Array.isArray(row.sides_resolved) ? row.sides_resolved : [],
    allergens_resolved: Array.isArray(row.allergens_resolved)
      ? row.allergens_resolved
      : [],
    raw: row,
  };
}

function currentUsername() {
  try {
    return (
      localStorage.getItem("loggedUser_Username") ||
      (typeof window !== "undefined" &&
        (window.CURRENT_USER_EMAIL || window.loggedUser_Username)) ||
      "admin"
    );
  } catch {
    return "admin";
  }
}

function adaptSide(row = {}) {
  return {
    id: String(row.id ?? ""),
    item: row.item ?? row.name ?? "",
    name: row.item ?? row.name ?? "",
    price: row.price ?? "0",
    description: row.description ?? "",
    quantity: row.quantity ?? 0,
    subcode_id: String(row.subcode_id ?? "0"),
    subcode_name: row.subcode_name ?? "",
    visibility: String(row.visibility ?? "1"),
    universal: String(row.universal ?? "0"),
    orders: row.orders ?? 0,
    raw: row,
  };
}

// ---- PUBLIC API (tenant-aware) ----

// Pulls everything and returns the nested structure your UI already expects
export async function fetchMenu(tenantServerUrl) {
  const root = menuRoot(tenantServerUrl);

  // 1) main categories
  const mainRes = await getJSON(`${root}/fetchMenuCategoriesBESA.php`);
  const rows = Array.isArray(mainRes?.categories)
    ? mainRes.categories
    : Array.isArray(mainRes)
    ? mainRes
    : [];
  const cats = rows.map(adaptCategory);

  // 2) subcategories for each category (parallel)
  const subPromises = cats.map(async (cat) => {
    const subRes = await getJSON(`${root}/fetchMenuSubcategoriesBESA.php`, {
      category: cat.code,
    });
    const subRows = Array.isArray(subRes?.subcategories)
      ? subRes.subcategories
      : Array.isArray(subRes)
      ? subRes
      : [];
    const subs = subRows.map(adaptSubcategory);

    // 3) items for each subcategory (parallel)
    const itemPromises = subs.map(async (sc) => {
      const itemsRes = await getJSON(`${root}/fetchMenuItemsBESA.php`, {
        subcode: sc.subcode,
      });
      const itemRows = Array.isArray(itemsRes?.items)
        ? itemsRes.items
        : Array.isArray(itemsRes)
        ? itemsRes
        : [];
      sc.items = itemRows.map(adaptItem);
      return sc;
    });

    cat.subcategories = await Promise.all(itemPromises);
    return cat;
  });

  const fullCats = await Promise.all(subPromises);
  return { categories: fullCats };
}

export async function editCategoryFields(tenantServerUrl, payload) {
  const root = menuRoot(tenantServerUrl);
  console.log("Sending edit cat payload:", JSON.stringify(payload));
  return postForm(`${root}/editMainMenuCategoryBESA.php`, payload);
}

export async function editSubcategoryFields(tenantServerUrl, payload) {
  const root = menuRoot(tenantServerUrl);
  console.log("Sending edit sub payload:", JSON.stringify(payload));
  return postForm(`${root}/editMenuSubcategoryBESA.php`, payload);
}

export async function editItemFields(tenantServerUrl, payload) {
  if (!payload?.id) throw new Error("item id required");
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/editMenuItemBESA.php`, payload);
}

// --------- UPDATE MENU ITEM (JSON via redir) ---------

/** Safe stringify that truncates large strings/objects to keep the console usable */
function safeStringify(obj, limit = 4000) {
  try {
    const json = JSON.stringify(
      obj,
      (k, v) => {
        if (typeof v === "string" && v.length > 512)
          return v.slice(0, 512) + "…(+truncated)";
        return v;
      },
      2
    );
    return json.length > limit ? json.slice(0, limit) + "\n…(+truncated)" : json;
  } catch {
    return String(obj);
  }
}

/** Simple random ID for correlating logs across requests */
function makeReqId() {
  return (
    Math.random().toString(36).slice(2, 8) +
    "-" +
    Date.now().toString(36)
  );
}

/**
 * Update menu item via redir.php (POST + JSON)
 */
export async function updateMenuItem(tenantServerUrl, body, opts = {}) {
  const debug = true;
  const requestId = makeReqId();
  const root = menuRoot(tenantServerUrl);
  const url = buildUrl(`${root}/editMenuItemBESA.php`);

  console.log("Menu item update payload:", JSON.stringify(body));
  console.log("sending to url:", url);

  const fetchOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Request-ID": requestId,
      "X-TM-Debug": debug ? "1" : "0",
    },
    body: JSON.stringify(body),
  };

  const t0 =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  if (debug) {
    console.groupCollapsed(
      `%c🛰️ updateMenuItem %c${requestId}`,
      "color:#444;font-weight:600",
      "color:#888"
    );
    console.log("→ URL:", url);
    console.log("→ Method:", fetchOptions.method);
    console.log("→ Headers:", fetchOptions.headers);
    try {
      console.log("→ Body:", JSON.parse(fetchOptions.body));
    } catch {
      console.log("→ Body(raw):", fetchOptions.body);
    }
  }

  let res;
  try {
    res = await fetch(url, fetchOptions);
  } catch (networkErr) {
    const t1 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    if (debug) {
      console.error("✖ Network error:", networkErr);
      console.log("⏱ Duration(ms):", Math.round(t1 - t0));
      console.groupEnd?.();
    }
    throw new Error(
      `Network error while updating menu item: ${
        networkErr?.message || networkErr
      }`
    );
  }

  const durationMs = Math.round(
    (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0
  );

  let text = "";
  try {
    text = await res.clone().text();
  } catch {
    // ignore
  }

  let data;
  try {
    data = text ? JSON.parse(text) : await res.json();
  } catch {
    data = { raw: text };
  }

  if (debug) {
    console.log("← Status:", res.status, res.ok ? "(OK)" : "(ERROR)");
    try {
      console.log("← Response headers:", Array.from(res.headers.entries()));
    } catch {}
    console.log("← Response (safe):", safeStringify(data));
    console.log("⏱ Duration(ms):", durationMs);
    console.groupEnd?.();
  }

  if (!res.ok) {
    const snippet =
      typeof text === "string"
        ? text.slice(0, 500)
        : safeStringify(data, 500);
    throw new Error(`Update failed (${res.status}): ${snippet}`);
  }

  return { data, meta: { status: res.status, durationMs, requestId } };
}

// ---- upload image ----
export async function uploadMenuImage(
  tenantServerUrl,
  file,
  { code, isCategory } = {}
) {
  if (!(file instanceof File)) {
    throw new Error("uploadMenuImage: 'file' must be a File");
  }

  const root = menuRoot(tenantServerUrl);
  const upstream = `${root}/uploadMenuItemImageBESA.php`;
  const full = buildUrl(upstream);

  const form = new FormData();
  form.append("photo", file, file.name);
  if (isCategory && code != null) form.append("code", String(code));

  const bywho =
    (typeof window !== "undefined" &&
      (window.CURRENT_USER_EMAIL || window.loggedUser_Email)) ||
    "admin";
  form.append("bywho", bywho);

  const res = await fetch(full, { method: "POST", body: form });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    console.error("[upload] non-JSON response:", text);
    throw new Error(`Upload failed (HTTP ${res.status})`);
  }

  if (!res.ok || !json.ok) {
    console.error("[upload] failed payload:", json);
    throw new Error(json.message || `Upload failed (HTTP ${res.status})`);
  }

  return json;
}

export async function searchMenuItemImages(
  tenantServerUrl,
  { q, description = "", limit = 12 } = {}
) {
  const query = String(q || "").trim();
  if (!query) throw new Error("Enter an item name to search for images.");
  const root = menuRoot(tenantServerUrl);
  const payload = await getJSON(`${root}/searchMenuItemImageBESA.php`, {
    q: query,
    description: String(description || "").trim(),
    limit,
  });
  if (payload?.ok === false) {
    throw new Error(payload?.message || "Image search failed");
  }
  const images = Array.isArray(payload?.images) ? payload.images : [];
  return { query: payload?.query || query, images };
}

export async function saveMenuItemImageFromUrl(
  tenantServerUrl,
  { imageUrl, filename, item, bywho, isCategory, code } = {}
) {
  if (!imageUrl) throw new Error("Missing image URL");
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/saveMenuItemImageFromUrlBESA.php`, {
    image_url: imageUrl,
    filename: filename || "",
    item: item || "",
    code: isCategory ? String(code || "") : "",
    is_category: isCategory ? "1" : "0",
    bywho:
      bywho ||
      (typeof window !== "undefined" &&
        (window.CURRENT_USER_EMAIL || window.loggedUser_Email)) ||
      "admin",
  });
}

// ---- basic form helpers for add/delete ----
function toForm(obj) {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => p.append(k, v ?? ""));
  return p;
}

// ——— Add Main Category ———
export async function addMainCategory(
  tenantServerUrl,
  { category, code, description, visibility, username }
) {
  const root = menuRoot(tenantServerUrl);
  const endpoint = `${root}/addCategoryBESA.php`;
  const payload = {
    category: (category || "").replace(/ /g, "__"),
    code,
    description: (description || "").replace(/ /g, "__"),
    visibility: String(visibility ? 1 : 0),
    username: username || "admin",
  };
  const body = toForm(payload);
  console.log("URL:", endpoint);
  console.log("Payload:", JSON.stringify(payload));

  const r = await fetch(buildUrl(endpoint), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body,
  });

  const text = (await r.text()).trim();
  console.log("RESULT:", text);
  return text; // "success" | "exists" | "failure"
}

// ——— Add Subcategory ———
export async function addSubcategoryApi(
  tenantServerUrl,
  { main, code, category, subcode, bywho, visibility }
) {
  const root = menuRoot(tenantServerUrl);
  const endpoint = `${root}/addSubCategoryBESA.php`;

  const payload = {
    main: (main || "").replace(/ /g, "__"),
    code,
    category,
    subcode,
    bywho: bywho || "admin",
    visibility: String(visibility ? 1 : 0),
  };

  const body = toForm(payload);
  console.log("URL:", endpoint);
  console.log("Payload:", JSON.stringify(payload));

  const r = await fetch(buildUrl(endpoint), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body,
  });

  const text = (await r.text()).trim();
  if (text !== "success" && text !== "exists") {
    throw new Error(`Subcategory add failed: ${text}`);
  }
  return text;
}

// ---- CREATE ITEM ----
export async function createMenuItem(tenantServerUrl, fields = {}) {
  const {
    code,
    subcode,
    item,
    price,
    price1,
    price2,
    description,
    visibility = 1,
    username,
    bywho,
    vat = 0,
    order = 0,
    available = 1,
    sides = "",
    sizes = "",
    specifications = "",
    url = "",
    rating = 3,
    quantity = 0,
    warning = 0,
    allergen_ids = "",
  } = fields;

  const root = menuRoot(tenantServerUrl);
  const endpoint = `${root}/addMenuItemBESA.php`;
  const payload = {
    code,
    subcode,
    item,
    price,
    price1,
    price2,
    description: (description || "").replace(/ /g, "__"),
    visibility: String(visibility ? 1 : 0),
    username: username || bywho || currentUsername(),
    bywho: bywho || username || currentUsername(),
    vat,
    order,
    available: String(available ? 1 : 0),
    sides,
    sizes,
    specifications,
    url,
    rating,
    quantity,
    warning,
    allergen_ids: Array.isArray(allergen_ids)
      ? allergen_ids.join(",")
      : allergen_ids,
  };
  console.log(payload);
  const body = toForm(payload);

  const r = await fetch(buildUrl(endpoint), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body,
  });

  const text = (await r.text()).trim();
  if (text.toLowerCase() !== "success" && text.toLowerCase() !== "exists") {
    throw new Error(`Item create failed: ${text}`);
  }
  return text;
}

// ----- deletes -----
async function postText(upstreamUrl, body) {
  const r = await fetch(buildUrl(upstreamUrl), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body,
  });
  const text = (await r.text()).trim();
  return text;
}

export async function deleteMainCategory(
  tenantServerUrl,
  { id, code, username }
) {
  const root = menuRoot(tenantServerUrl);
  const upstream = `${root}/deleteCategoryBESA.php`;

  const body = toForm({
    id: id ?? "",
    code: code ?? "",
    bywho:
      username ||
      localStorage.getItem("loggedUser_Username") ||
      "admin",
  });

  const text = await postText(upstream, body);
  console.log("Result:", JSON.stringify(text));
  if (text !== "success")
    throw new Error(`Category delete failed: ${text}`);
  return text;
}

export async function deleteSubcategoryApi(
  tenantServerUrl,
  { id, code, subcode, username }
) {
  const root = menuRoot(tenantServerUrl);
  const upstream = `${root}/deleteSubCategoryBESA.php`;

  const body = toForm({
    id: id ?? "",
    code: code ?? "",
    subcode: subcode ?? "",
    bywho:
      username ||
      localStorage.getItem("loggedUser_Username") ||
      "admin",
  });

  const text = await postText(upstream, body);
  console.log("Result:", JSON.stringify(text));
  if (text !== "success")
    throw new Error(`Subcategory delete failed: ${text}`);
  return text;
}

export async function deleteMenuItem(
  tenantServerUrl,
  { id, username }
) {
  const root = menuRoot(tenantServerUrl);
  const upstream = `${root}/deleteMenuItemBESA.php`;

  const body = toForm({
    id,
    bywho:
      username ||
      localStorage.getItem("loggedUser_Username") ||
      "admin",
  });

  const text = await postText(upstream, body);
  console.log("Result:", JSON.stringify(text));
  if (text !== "success") throw new Error(`Item delete failed: ${text}`);
  return text;
}

// ---- import/export helpers (local only) ----
let LOCAL_MENU_CACHE = { categories: [] };

export async function importMenuLocal(jsonText) {
  const parsed = JSON.parse(jsonText);
  const cats = Array.isArray(parsed?.categories)
    ? parsed.categories
    : Array.isArray(parsed)
    ? parsed
    : null;
  if (!cats) throw new Error("Invalid format: expected { categories: [...] }");
  LOCAL_MENU_CACHE = { categories: cats };
  return JSON.parse(JSON.stringify(LOCAL_MENU_CACHE));
}

export async function exportMenuLocal() {
  return JSON.stringify(LOCAL_MENU_CACHE, null, 2);
}
export { importMenuLocal as importMenu, exportMenuLocal as exportMenu };

// ---- Universal / shared sides ----
export async function fetchSideMenuItems(
  tenantServerUrl,
  { universal = true, subcode = "" } = {}
) {
  const root = menuRoot(tenantServerUrl);
  const qs = {};
  if (universal === "all") qs.universal = "all";
  else qs.universal = universal ? "1" : "0";
  if (subcode) qs.subcode = subcode;

  const payload = await getJSON(`${root}/fetchSideMenuItemsBESA.php`, qs);
  const rows = Array.isArray(payload?.sides)
    ? payload.sides
    : Array.isArray(payload)
    ? payload
    : [];
  return rows.map(adaptSide);
}

export async function addSideMenuItem(tenantServerUrl, fields = {}) {
  const root = menuRoot(tenantServerUrl);
  const payload = {
    item: fields.item ?? fields.name ?? "",
    price: fields.price ?? "0",
    description: fields.description ?? "",
    quantity: fields.quantity ?? 100,
    subcode_id: fields.subcode_id ?? fields.subcode ?? 0,
    visibility: fields.visibility ? 1 : 0,
    universal: fields.universal === false || fields.universal === 0 ? 0 : 1,
    bywho: fields.bywho || currentUsername(),
  };
  return postForm(`${root}/addSidesMenuItemBESA.php`, payload);
}

export async function editSideMenuItem(tenantServerUrl, fields = {}) {
  if (!fields?.id) throw new Error("side id required");
  const root = menuRoot(tenantServerUrl);
  const payload = {
    id: fields.id,
    item: fields.item ?? fields.name ?? "",
    price: fields.price ?? "0",
    description: fields.description ?? "",
    quantity: fields.quantity ?? 0,
    subcode_id: fields.subcode_id ?? fields.subcode ?? 0,
    visibility: fields.visibility ? 1 : 0,
    universal: fields.universal === false || fields.universal === 0 ? 0 : 1,
    bywho: fields.bywho || currentUsername(),
  };
  return postForm(`${root}/editSidesMenuItemBESA.php`, payload);
}

export async function deleteSideMenuItem(tenantServerUrl, { id, username } = {}) {
  if (!id) throw new Error("side id required");
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/deleteSideMenuItemBESA.php`, {
    id,
    bywho: username || currentUsername(),
  });
}

function adaptAllergen(row = {}) {
  const numeric = String(
    row.allergen_id ?? row.id ?? row.ID ?? row[0] ?? ""
  ).trim();
  const id =
    numeric && numeric !== "0" && numeric !== "undefined" && numeric !== "null"
      ? numeric
      : "";
  return {
    id,
    title: row.title ?? row.name ?? row[1] ?? "",
    description: row.description ?? row[2] ?? "",
    icon: row.icon ?? row[3] ?? "none.jpg",
    item_count: Number(row.item_count ?? row[4] ?? 0),
    raw: row,
  };
}

export async function fetchAllergens(tenantServerUrl) {
  const root = menuRoot(tenantServerUrl);
  const payload = await getJSON(`${root}/fetchAllergensBESA.php`);
  const rows = Array.isArray(payload?.allergens)
    ? payload.allergens
    : Array.isArray(payload)
    ? payload
    : [];
  return rows.map(adaptAllergen);
}

export async function addAllergen(tenantServerUrl, fields = {}) {
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/addAllergenBESA.php`, {
    title: fields.title ?? fields.name ?? "",
    description: fields.description ?? "",
    icon: fields.icon ?? "none.jpg",
    bywho: fields.bywho || currentUsername(),
  });
}

export async function editAllergen(tenantServerUrl, fields = {}) {
  if (!fields?.id) throw new Error("allergen id required");
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/editAllergenBESA.php`, {
    id: fields.id,
    title: fields.title ?? fields.name ?? "",
    description: fields.description ?? "",
    icon: fields.icon ?? "none.jpg",
    bywho: fields.bywho || currentUsername(),
  });
}

export async function deleteAllergen(tenantServerUrl, { id, username } = {}) {
  if (!id) throw new Error("allergen id required");
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/deleteAllergenBESA.php`, {
    id,
    bywho: username || currentUsername(),
  });
}

export async function dumpMenuSQL(tenantServerUrl) {
  const root = menuRoot(tenantServerUrl);
  const payload = await getJSON(`${root}/dumpMenuSQLBESA.php`);
  if (!payload?.ok || typeof payload.sql !== "string") {
    throw new Error(payload?.message || "Menu SQL dump failed");
  }
  return payload;
}

export async function clearMenu(tenantServerUrl, { username, confirm = "CLEAR" } = {}) {
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/clearMenuBESA.php`, {
    confirm,
    bywho: username || currentUsername(),
  });
}

export async function resetMenuItemRatings(
  tenantServerUrl,
  { username, confirm = "RESET" } = {}
) {
  const root = menuRoot(tenantServerUrl);
  return postForm(`${root}/resetMenuItemRatingsBESA.php`, {
    confirm,
    bywho: username || currentUsername(),
  });
}